/* eslint-disable max-len */

export type DatabaseIdAlphabet = 'UC_LETTERS' | 'UC_LETTERS_DIGITS' | 'LC_LETTERS' | 'DIGITS' | 'ALL' | 'NONSIMILAR_CHARACTERS';

export class DatabaseIdGenerator {
  private static readonly UC_LETTERS: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  private static readonly LC_LETTERS: string = 'abcdefghijklmnopqrstuvwxyz';
  private static readonly DIGITS: string = '0123456789';
  private static readonly NONSIMILAR_CHARACTERS: string = 'ABCDEFGHJKLMNPQRSTWXYZ23456789';

  static readonly kAutoIdLength: number = 20;
  static readonly kAuthIdLength: number = 28;

  constructor(private readonly getRandomValues: (typedArray: Uint8Array<ArrayBuffer>) => Uint8Array<ArrayBuffer> | Uint8ClampedArray<ArrayBuffer>) {
  }

  /**
   * Generates a unique ID for a Cloud Firestore document.
   * Copied from Cloud Firestore library source code.
   *
   * @param {{length: number}} options
   * @return {string}
   */
  generate(options?: {length?: number, alphabet?: DatabaseIdAlphabet}): string {
    const idLength = options?.length ?? DatabaseIdGenerator.kAutoIdLength;
    const alphabet = this.generateAlphabet(options?.alphabet ?? 'ALL');
    const alphabetSize = alphabet.length;

    let autoId = '';

    while(autoId.length < idLength) {
      const array = new Uint8Array(DatabaseIdGenerator.kAutoIdLength);
      const bytes = this.getRandomValues(array);
      bytes.forEach((b) => {
        // Length of `chars` is 62. We only take bytes between 0 and 62*4-1
        // (both inclusive). The value is then evenly mapped to indices of `char`
        // via a modulo operation.
        const maxValue = alphabetSize * 4 - 1;
        if(autoId.length < idLength && b <= maxValue) {
          autoId += alphabet.charAt(b % alphabetSize);
        }
      });
    }

    return autoId;
  }

  private generateAlphabet(type: DatabaseIdAlphabet): string {
    switch(type) {
      case 'UC_LETTERS':
        return DatabaseIdGenerator.UC_LETTERS;
      case 'UC_LETTERS_DIGITS':
        return DatabaseIdGenerator.UC_LETTERS + DatabaseIdGenerator.DIGITS;
      case 'LC_LETTERS':
        return DatabaseIdGenerator.LC_LETTERS;
      case 'DIGITS':
        return DatabaseIdGenerator.DIGITS;
      case 'NONSIMILAR_CHARACTERS':
        return DatabaseIdGenerator.NONSIMILAR_CHARACTERS;
      default:
        return DatabaseIdGenerator.UC_LETTERS + DatabaseIdGenerator.LC_LETTERS + DatabaseIdGenerator.DIGITS;
    }
  }
}
